import { Link, router, usePage } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, Edit, Archive, Search, UserCheck, UserMinus } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Props, Tab, STAGE_LABELS } from "./_types";
import OverviewTab from "./OverviewTab";
import SubmissionsTab from "./SubmissionsTab";
import SkmTab from "./SkmTab";
import ActivityTab from "./ActivityTab";
import CoverLettersTab from "./CoverLettersTab";
import ExclusionsTab from "./ExclusionsTab";
import EmailComposer from "../../components/EmailComposer";

export default function CandidateShow({ candidate, dnc_logs, submissions, skm_blasts, resume_url, package_summary, matching_jobs, practice_area_options, ai_assist }: Props) {
  const { auth } = usePage<{ auth: { user: { role: string } } }>().props;
  const isAdmin = auth?.user?.role === "admin";
  const [tab, setTab] = useState<Tab>("overview");
  const [bgOpen, setBgOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [addToDnc, setAddToDnc] = useState(false);
  const [matching, setMatching] = useState(false);

  // Matching-Jobs filters (task ae6399ac). Default to the candidate's own market
  // + practice area per Steven; he refines from there. Hard-gated server-side.
  const [locations, setLocations] = useState<string[]>(candidate.location ? [candidate.location] : []);
  const [practiceAreas, setPracticeAreas] = useState<string[]>(candidate.practice_area ? [candidate.practice_area] : []);
  const [includeRemote, setIncludeRemote] = useState(false);

  // On-demand reverse match (task bd8200b9) — matches this candidate against
  // all current open roles right now, async like the job-side Re-match
  // button. Lives in the page header, not buried in a tab — this is the
  // action people look for right after adding a candidate. Filters (task
  // ae6399ac) are edited on the Matching Jobs tab and sent along.
  function findMatchingRoles() {
    setMatching(true);
    router.post(`/candidates/${candidate.id}/match_jobs`, {
      locations,
      practice_areas: practiceAreas,
      include_remote: includeRemote,
    }, {
      onFinish: () => setMatching(false),
    });
  }

  const priorityColors: Record<string, string> = {
    high: "bg-red-100 text-red-700", normal: "bg-gray-100 text-gray-600", low: "bg-blue-50 text-blue-600",
  };

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "overview",    label: "Overview" },
    { key: "resume",      label: "Resume" },
    { key: "submissions", label: "Submissions", count: submissions.length },
    { key: "skm",         label: "SKM",         count: skm_blasts.length },
    { key: "activity",      label: "Activity",      count: dnc_logs.length },
    { key: "cover_letters", label: "Matching Jobs", count: matching_jobs.length > 0 ? matching_jobs.length : undefined },
    { key: "email",         label: "Email" },
    { key: "exclusions",    label: "Exclusions", count: candidate.excluded_firms?.length > 0 ? candidate.excluded_firms.length : undefined },
  ];

  return (
    <AppLayout title={candidate.full_name}>
      {/* Header */}
      <div className="mb-5">
        <Link href="/candidates" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Candidates
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{candidate.full_name}</h1>
            <p className="text-gray-500 mt-0.5">
              {[candidate.candidate_type, candidate.firm_name, candidate.location].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${priorityColors[candidate.priority] ?? priorityColors.normal}`}
              title="Priority bumps this candidate to the top of pipeline columns and the dashboard's high-priority list. Set in Edit."
            >
              Priority: {candidate.priority}
            </span>
            <Badge
              variant="outline"
              title="Current step in the recruiting pipeline. Drag the card on the Pipeline page to move between stages."
            >
              Stage: {STAGE_LABELS[candidate.pipeline_stage] ?? candidate.pipeline_stage}
            </Badge>

            {/* Lifecycle actions */}
            {candidate.lifecycle_state === "active" && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { if (confirm("Mark this candidate as inactive?")) router.post(`/candidates/${candidate.id}/mark_inactive`); }}
                >
                  <UserMinus className="w-4 h-4 mr-1" /> Mark Inactive
                </Button>
                <Button
                  variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setArchiveOpen(true)}
                >
                  <Archive className="w-4 h-4 mr-1" /> Archive…
                </Button>
              </>
            )}
            {candidate.lifecycle_state === "inactive" && (
              <>
                <Button
                  variant="outline" size="sm" className="text-green-700 border-green-200 hover:bg-green-50"
                  onClick={() => router.post(`/candidates/${candidate.id}/reactivate`)}
                >
                  <UserCheck className="w-4 h-4 mr-1" /> Reactivate
                </Button>
                <Button
                  variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setArchiveOpen(true)}
                >
                  <Archive className="w-4 h-4 mr-1" /> Archive…
                </Button>
              </>
            )}
            {candidate.lifecycle_state === "archived" && (
              <Button
                variant="outline" size="sm" className="text-green-700 border-green-200 hover:bg-green-50"
                onClick={() => router.post(`/candidates/${candidate.id}/reactivate`)}
              >
                <UserCheck className="w-4 h-4 mr-1" /> Reactivate
              </Button>
            )}

            {ai_assist && (
              <Button variant="outline" size="sm" onClick={findMatchingRoles} disabled={matching}>
                <Search className="w-4 h-4 mr-1" /> {matching ? "Matching..." : "Find Matching Roles"}
              </Button>
            )}

            <Link href={`/candidates/${candidate.id}/edit`}>
              <Button size="sm" className="border-2 border-gray-400 hover:border-gray-600"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Lifecycle state banner */}
      {candidate.lifecycle_state !== "active" && (
        <div className={`mb-4 rounded-md px-4 py-2.5 text-sm flex items-center gap-2 ${
          candidate.lifecycle_state === "archived"
            ? "bg-red-50 border border-red-200 text-red-700"
            : "bg-yellow-50 border border-yellow-200 text-yellow-700"
        }`}>
          {candidate.lifecycle_state === "inactive"
            ? "This candidate is marked inactive and hidden from the active roster."
            : `Archived — ${candidate.archived_reason || "no reason recorded"}`}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className="ml-1.5 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab
          candidate={candidate}
          bgOpen={bgOpen}
          setBgOpen={setBgOpen}
          package_summary={package_summary}
        />
      )}

      {tab === "resume" && (
        <div>
          {resume_url ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Link href={`/candidates/${candidate.id}/resume-optimizer`}>
                  <Button variant="outline" size="sm">Optimize Resume</Button>
                </Link>
              </div>
              <div className="rounded-lg border border-gray-200 overflow-hidden" style={{ height: "75vh" }}>
                <iframe src={resume_url} className="w-full h-full" title="Resume" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-gray-400 text-sm mb-3">No resume uploaded yet.</p>
              <Link href={`/candidates/${candidate.id}/package`}>
                <Button variant="outline" size="sm">Upload via Package</Button>
              </Link>
            </div>
          )}
        </div>
      )}

      {tab === "submissions" && <SubmissionsTab candidateId={candidate.id} submissions={submissions} aiAssist={ai_assist} />}
      {tab === "skm"         && <SkmTab skm_blasts={skm_blasts} />}
      {tab === "activity"      && <ActivityTab dnc_logs={dnc_logs} />}

      {tab === "cover_letters" && (
        <CoverLettersTab
          candidateId={candidate.id}
          candidateFirstName={candidate.preferred_first_name || candidate.first_name}
          candidateEmail={candidate.email}
          matchingJobs={matching_jobs}
          aiAssist={ai_assist}
          practiceAreaOptions={practice_area_options}
          locations={locations}
          onLocationsChange={setLocations}
          practiceAreas={practiceAreas}
          onPracticeAreasChange={setPracticeAreas}
          includeRemote={includeRemote}
          onIncludeRemoteChange={setIncludeRemote}
          onFindMatches={findMatchingRoles}
          matching={matching}
        />
      )}

      {tab === "exclusions" && (
        <ExclusionsTab
          candidateId={candidate.id}
          excludedFirms={candidate.excluded_firms ?? []}
        />
      )}

      {tab === "email" && (
        <EmailComposer
          templateTypes={["candidate_outreach", "resume_permission", "interview_coordination"]}
          defaultType="candidate_outreach"
          aiAssist={ai_assist}
          recipientEmail={candidate.email}
          context={{
            candidate: {
              first_name:    candidate.first_name,
              last_name:     candidate.last_name,
              firm_name:     candidate.firm_name,
              practice_area: candidate.practice_area,
              jd_year:       candidate.jd_year,
              location:      candidate.location,
              email:         candidate.email,
              law_school:    candidate.law_school,
            },
            target: {
              first_name: candidate.first_name,
              last_name:  candidate.last_name,
              firm:       candidate.firm_name,
              email:      candidate.email,
            },
          }}
        />
      )}
      {/* Archive modal */}
      {archiveOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setArchiveOpen(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Archive {candidate.full_name}</h2>
            <p className="text-sm text-gray-500 mb-4">Archived candidates are removed from the active roster. This is reversible.</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                rows={3}
                value={archiveReason}
                onChange={e => setArchiveReason(e.target.value)}
                placeholder="e.g. Opted out, wrong contact, no longer a fit"
                autoFocus
              />
            </div>

            {candidate.firm_name && (
              <div className="flex items-center gap-2 mb-6">
                <input
                  type="checkbox"
                  id="add_to_dnc"
                  checked={addToDnc}
                  onChange={e => setAddToDnc(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="add_to_dnc" className="text-sm text-gray-700">
                  Also add <span className="font-medium">{candidate.firm_name}</span> to the Do-Not-Contact list
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => { setArchiveOpen(false); setArchiveReason(""); setAddToDnc(false); }}
              >
                Cancel
              </Button>
              <Button
                variant="outline" size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
                disabled={!archiveReason.trim()}
                onClick={() => {
                  router.post(`/candidates/${candidate.id}/archive`, {
                    reason:     archiveReason,
                    add_to_dnc: addToDnc ? "1" : "0",
                  });
                  setArchiveOpen(false);
                  setArchiveReason("");
                  setAddToDnc(false);
                }}
              >
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
